import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Button,
  DataTable,
  Field,
  Input,
  Kpi,
  Pager,
  StateBlock,
  type Column,
} from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'
import { isoDay } from '@/lib/format'
import {
  formatLiveCasinoFinalOutcome,
  formatLiveCasinoNet,
  liveCasinoPageCurrency,
} from './liveCasinoDisplay'
import './vendor-dashboard.css'

type Row = {
  url: string
  createdAt: string
  userId: string
  currency: string
  gameId: string
  transactionId: string
  winLoseAmt: number
}

/**
 * Vendor Dashboard → Live Casino from Strix BQ casino.transactions
 * (CM-API `POST /v1/vendor/live-casino/bq-detail`).
 */
export function LiveCasinoPage() {
  const today = isoDay(new Date())
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [applied, setApplied] = useState({ from: today, to: today, run: 0 })
  const [page, setPage] = useState(1)
  const pageSize = DEFAULT_PAGE_SIZE

  const q = useQuery({
    // `run` bumps on every Get Details click so same dates still refetch.
    queryKey: [
      'cm',
      'vendor',
      'live-casino',
      'bq-detail',
      applied.from,
      applied.to,
      applied.run,
      page,
      pageSize,
    ],
    queryFn: () =>
      cmApi.liveCasinoBqDetail(authHeaders(), {
        fromDate: applied.from,
        toDate: applied.to,
        page,
        pageSize,
      }),
  })

  const rows = q.data?.rows ?? []
  const total = q.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const finalOutcome = q.data?.finalOutcomeAmount ?? 0
  const pageCurrency = liveCasinoPageCurrency(rows)
  const finalOutcomeLabel =
    q.isLoading && !q.data
      ? '—'
      : formatLiveCasinoFinalOutcome(finalOutcome, rows)

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        id: 'n',
        header: 'No.',
        width: 56,
        numeric: true,
        cell: (_r, i) => String((page - 1) * pageSize + i + 1),
      },
      {
        id: 'createdAt',
        header: 'Created At',
        cell: (r) =>
          r.createdAt ? new Date(r.createdAt).toLocaleString() : '—',
      },
      { id: 'userId', header: 'User ID', cell: (r) => r.userId || '—' },
      { id: 'currency', header: 'Currency', cell: (r) => r.currency || '—' },
      { id: 'gameId', header: 'Game ID', cell: (r) => r.gameId || '—' },
      {
        id: 'txn',
        header: 'Transaction ID',
        cell: (r) => r.transactionId || '—',
      },
      {
        id: 'outcome',
        header: 'Outcome',
        numeric: true,
        cell: (r) => {
          const label = formatLiveCasinoNet(r.winLoseAmt, r.currency)
          const n = r.winLoseAmt
          const cls =
            n > 0 ? 'cm-money-pos' : n < 0 ? 'cm-money-neg' : undefined
          return <span className={cls}>{label}</span>
        },
      },
    ],
    [page, pageSize],
  )

  return (
    <CmPanel
      title="Live Casino"
      subtitle="Vendor Dashboard — Live Casino from BigQuery"
      wide
      actions={
        <Button
          type="button"
          variant="secondary"
          busy={q.isFetching}
          onClick={() => void q.refetch()}
        >
          Refresh
        </Button>
      }
    >
      <div className="cm-vendor-filters">
        <Field label="From Date" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="date"
              required
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          )}
        </Field>
        <Field label="To Date" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="date"
              required
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          )}
        </Field>
        <div className="cm-vendor-filters-actions">
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              setPage(1)
              setApplied((prev) => ({
                from: fromDate,
                to: toDate,
                run: prev.run + 1,
              }))
            }}
          >
            Get Details
          </Button>
        </div>
        <div className="cm-vendor-filters-kpi">
          <Kpi
            label="Final Outcome Amount"
            value={finalOutcomeLabel}
            meta={
              pageCurrency === 'INR'
                ? 'Sum of player net for range (INR major when mixed)'
                : `Sum of player net for range (${pageCurrency})`
            }
          />
        </div>
      </div>

      {q.isLoading && !rows.length ? (
        <StateBlock icon="live" title="Loading Live Casino…">
          Reading Live Casino transactions
        </StateBlock>
      ) : null}

      {!q.isLoading && rows.length === 0 ? (
        <StateBlock icon="search" title="No data found">
          No Live Casino transactions in this date range.
        </StateBlock>
      ) : null}

      {rows.length > 0 ? (
        <>
          <DataTable
            label="Live Casino transactions"
            columns={columns}
            rows={rows}
            rowKey={(r, i) => `${r.transactionId}-${i}`}
          />
          <Pager
            page={page}
            pageCount={pageCount}
            total={total}
            pageSize={pageSize}
            onChange={setPage}
          />
        </>
      ) : null}
    </CmPanel>
  )
}
